// Editar = criar: a lógica PURA da tela de edição (o repo não tem jsdom — regra
// dentro de componente é regra que ninguém testa). O diálogo "Nova operação" abre
// em modo editar com `formDaOperacao`, e ao salvar manda só o que mudou
// (`diffDaEdicao`): nada mudou = nenhuma chamada.

export type PrioridadeForm = "alta" | "normal" | "baixa";

export interface FormOperacao {
  titulo: string;
  frenteId: string;
  /** Id do responsável ("" = ninguém / o da frente). */
  responsavelId: string;
  semData: boolean;
  dataPrevista: string;
  prioridade: PrioridadeForm;
  local: string;
  atividadeId: string;
  setorIds: string[];
  itens: Array<{ mnjProdutoId: string; qtd: string }>;
  observacao: string;
}

export interface OperacaoParaForm {
  titulo: string | null;
  frenteId: string | null;
  status: "backlog" | "planejada" | "em_andamento" | "concluida" | "cancelada";
  dataPrevista: string | null;
  prioridade: PrioridadeForm;
  local: string | null;
  atividadeId: string | null;
  observacao: string | null;
  executores: Array<{ funcionarioId: string; papel: string }>;
  setores: Array<{ setorId: string }>;
  itens: Array<{ mnjProdutoId: string; qtdPrevista: string | number }>;
}

/** O que a tela pré-preenche — tudo o que a operação já tem, inclusive setores e insumos. */
export function formDaOperacao(op: OperacaoParaForm, hoje: string): FormOperacao {
  const atribuido = op.executores.filter(e => e.papel === "atribuido")[0];
  return {
    titulo: op.titulo ?? "",
    frenteId: op.frenteId ?? "",
    responsavelId: atribuido ? atribuido.funcionarioId : "",
    semData: op.status === "backlog",
    dataPrevista: op.dataPrevista ?? hoje,
    prioridade: op.prioridade,
    local: op.local ?? "",
    atividadeId: op.atividadeId ?? "",
    setorIds: op.setores.map(s => s.setorId),
    itens: op.itens.map(it => ({ mnjProdutoId: it.mnjProdutoId, qtd: String(Number(it.qtdPrevista)) })),
    observacao: op.observacao ?? "",
  };
}

export interface BloqueiosDaEdicao {
  /** Atividade, setores, insumos e frente. */
  estrutura: boolean;
  /** Data / backlog. */
  data: boolean;
  aviso: string | null;
}

/**
 * Operação encerrada só aceita título, local, observação e prioridade (o servidor
 * responde 409 no resto). Em andamento: a data não muda (reagendar é do que ainda
 * não começou).
 */
export function bloqueiosDaEdicao(status: OperacaoParaForm["status"]): BloqueiosDaEdicao {
  if (status === "concluida" || status === "cancelada") {
    return { estrutura: true, data: true, aviso: "Reabra a operação para alterar setores, insumos ou data." };
  }
  if (status === "em_andamento") return { estrutura: false, data: true, aviso: "Em andamento: a data não muda." };
  return { estrutura: false, data: false, aviso: null };
}

export interface DiffDaEdicao {
  /** Corpo do `PATCH /operacoes/:id` — só o que mudou. Vazio = nada a gravar. */
  campos: Record<string, unknown>;
  /** Transição de data/backlog (`PATCH /status`), à parte: a data é transição. */
  data: { status: "planejada" | "backlog"; dataPrevista?: string } | null;
}

const igualConjunto = (a: string[], b: string[]) => a.slice().sort().join(",") === b.slice().sort().join(",");

export function diffDaEdicao(op: OperacaoParaForm, form: FormOperacao, hoje: string): DiffDaEdicao {
  const antes = formDaOperacao(op, hoje);
  const bloq = bloqueiosDaEdicao(op.status);
  const campos: Record<string, unknown> = {};

  if (form.titulo.trim() !== antes.titulo.trim()) campos.titulo = form.titulo.trim() ? form.titulo.trim() : null;
  if (form.observacao.trim() !== antes.observacao.trim()) campos.observacao = form.observacao.trim() ? form.observacao.trim() : null;
  if (form.local.trim() !== antes.local.trim()) campos.local = form.local.trim() ? form.local.trim() : null;
  if (form.prioridade !== antes.prioridade) campos.prioridade = form.prioridade;

  if (!bloq.estrutura) {
    if (form.frenteId !== antes.frenteId && form.frenteId) campos.frenteId = form.frenteId;
    if (form.atividadeId !== antes.atividadeId) campos.atividadeId = form.atividadeId ? form.atividadeId : null;
    if (!igualConjunto(form.setorIds, antes.setorIds)) campos.setores = form.setorIds.map(setorId => ({ setorId }));
    const chave = (l: FormOperacao["itens"]) => l.map(i => `${i.mnjProdutoId}:${Number(i.qtd)}`);
    if (!igualConjunto(chave(form.itens), chave(antes.itens))) {
      campos.itens = form.itens.map(i => ({ mnjProdutoId: i.mnjProdutoId, qtdPrevista: Number(i.qtd) }));
    }
  }
  if (form.responsavelId !== antes.responsavelId) {
    campos.atribuidos = form.responsavelId ? [form.responsavelId] : [];
  }

  let data: DiffDaEdicao["data"] = null;
  if (!bloq.data) {
    const eraBacklog = op.status === "backlog";
    if (form.semData && !eraBacklog) data = { status: "backlog" };
    else if (!form.semData && (eraBacklog || form.dataPrevista !== (op.dataPrevista ?? ""))) {
      data = { status: "planejada", dataPrevista: form.dataPrevista };
    }
  }
  return { campos, data };
}

/** Salvar sem mudança: nenhuma chamada, só fecha. */
export function nadaMudou(d: DiffDaEdicao): boolean {
  return Object.keys(d.campos).length === 0 && d.data === null;
}

/**
 * Reagendar pelo detalhe (o operador não tem `manejo.planejar`, então não passa pela
 * tela de edição). Só o que ainda não começou: a mesma regra de `bloqueiosDaEdicao`.
 */
export function podeMudarData(status: OperacaoParaForm["status"]): boolean {
  return !bloqueiosDaEdicao(status).data;
}

/** A data nova é válida para gravar? (preenchida e diferente da atual) */
export function dataNovaValida(atual: string | null, nova: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(nova) && nova !== atual;
}

/** "2026-10-30" → "30/10/2026"; sem data → "sem data (backlog)". */
export function dataPrevistaLegivel(iso: string | null): string {
  if (!iso) return "sem data (backlog)";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}
